import React from 'react';

import {
  BOOKS_CROSS_ORG_TEXT,
  BOOKS_OFF_TEXT,
  BOOKS_STATUS_UNAVAILABLE_TEXT,
  booksBehindLine,
  booksExceptionsLine,
  booksLastProblemLine,
  booksPostedLine,
} from '../../constants/busyFile';

// STATUS lines at the top of "Monthly files" (BUSY-VOUCHER R3-S5). Pure
// presentational: the parent passes the `books` object + `books_status_error`
// of GET /reports/monthly-files (R3 build plan §2.1). No fetching, no state.
// Every line's WORDING comes from constants/busyFile.js (one source, shared
// with the row notes); this file only decides which lines show and how they
// look. ADMIN and ACCOUNTANT see identical lines (OR-33). No patient data in
// any line.
//
// Look = r3/monthly-files.html status frame (bk-2195): ONE status line with a
// dot (green = posting normally, grey = waiting / not readable, amber = a
// problem below), all problems inside ONE amber box (one line each), and while
// the cross-organisation safety stop is on ONLY the red box shows (the stop's
// reason is not repeated as "Last problem"). The dot never carries meaning
// alone: every state is also words.

const LINE = 'flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200';
const NOTE_BOX = 'rounded-lg border border-dashed border-slate-400 bg-slate-100 px-3 py-2 text-sm text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';
const AMBER_BOX = 'rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100';
const RED_BOX = 'rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-800 dark:border-rose-700 dark:bg-rose-900/40 dark:text-rose-100';
// Lines after the first inside the one amber box are separated by a thin rule.
const AMBER_DIVIDER = 'mt-1.5 border-t border-amber-300 pt-1.5 dark:border-amber-700';
const DOT = {
  green: 'bg-green-500',
  grey: 'bg-slate-400 dark:bg-slate-500',
  amber: 'bg-amber-500',
};

function StatusDot({ tone }) {
  return <span aria-hidden="true" data-testid="books-status-dot" data-tone={tone} className={`inline-block h-2 w-2 shrink-0 rounded-full ${DOT[tone]}`} />;
}

/**
 * @param {object|null} books `books` from the monthly-files response (null when the
 *   status read failed). Fields used: enabled, start_date, posted_up_to, days_behind,
 *   last_error {code, at, period}, attempts_since_success, open_exceptions, cross_org_stop.
 * @param {string|null} booksStatusError `books_status_error` ("STATUS_UNAVAILABLE" or null).
 */
export default function BooksStatusBlock({ books, booksStatusError }) {
  if (booksStatusError) {
    return (
      <div data-testid="books-status-block" className="mb-3">
        <p data-testid="books-status-unavailable" className={`${LINE} italic text-slate-500 dark:text-slate-400`}>
          <StatusDot tone="grey" />
          {BOOKS_STATUS_UNAVAILABLE_TEXT}
        </p>
      </div>
    );
  }
  if (!books) return null;

  // Books/files switched off for this organisation (OR-75): say why new months
  // have no file instead of hiding the block (copy review X8 + owner edit).
  if (!books.enabled) {
    return (
      <div data-testid="books-status-block" className="mb-3">
        <p data-testid="books-status-off" className={NOTE_BOX}>{BOOKS_OFF_TEXT}</p>
      </div>
    );
  }

  const postedLine = booksPostedLine(books);
  // While the safety stop is on, the red box is the whole story: the amber
  // lines (behind / last problem / entries) are not shown beside it.
  const amberLines = books.cross_org_stop ? [] : [
    ['books-status-behind', booksBehindLine(books)],
    ['books-status-problem', booksLastProblemLine(books.last_error, books.attempts_since_success)],
    ['books-status-exceptions', booksExceptionsLine(books.open_exceptions)],
  ].filter(([, text]) => text);
  const hasProblem = books.cross_org_stop || amberLines.length > 0;
  const dotTone = hasProblem ? 'amber' : (books.posted_up_to ? 'green' : 'grey');

  return (
    <div data-testid="books-status-block" className="mb-3 space-y-1.5">
      {postedLine && (
        <p data-testid={books.posted_up_to ? 'books-status-posted' : 'books-status-start'} className={LINE}>
          <StatusDot tone={dotTone} />
          {postedLine}
        </p>
      )}

      {amberLines.length > 0 && (
        <div role="alert" data-testid="books-status-alert" className={AMBER_BOX}>
          {amberLines.map(([testId, text], i) => (
            <p key={testId} data-testid={testId} className={i > 0 ? AMBER_DIVIDER : undefined}>{text}</p>
          ))}
        </div>
      )}

      {books.cross_org_stop && (
        <p role="alert" data-testid="books-status-cross-org" className={RED_BOX}>{BOOKS_CROSS_ORG_TEXT}</p>
      )}
    </div>
  );
}
